import React, { useEffect, useState } from 'react';
import { Path, useWatch } from 'react-hook-form';
import { Box, IconButton, Stack, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useFormContext } from 'react-hook-form';
import { ReviewFormValues } from './types';
import { Media } from '../../types/user';

interface PhotoPreviewsProps {
    name: Path<ReviewFormValues>;
    disabled?: boolean;
}

const PhotoPreviews: React.FC<PhotoPreviewsProps> = ({ name, disabled }) => {
    const { control, setValue } = useFormContext<ReviewFormValues>();
    const items: (File | Media)[] = useWatch({ control, name }) ?? [];
    const [urls, setUrls] = useState<string[]>([]);
    const theme = useTheme();

    useEffect(() => {
        const generateUrls = async () => {
            const newUrls: string[] = [];

            for (const item of items) {
                if (item instanceof File) {
                    newUrls.push(URL.createObjectURL(item));
                } else if (item && typeof item === 'object' && 'src' in item) {
                    newUrls.push(item.src);
                } else {
                    newUrls.push('');
                }
            }

            setUrls(newUrls);
        };

        generateUrls();

        return () => {
            urls.forEach((url) => {
                if (url.startsWith('blob:')) {
                    URL.revokeObjectURL(url);
                }
            });
        };
    }, [items]);

    const removeAt = (idx: number) => {
        if (disabled) return;
        const next = items.filter((_, i) => i !== idx);
        setValue(name, next, { shouldDirty: true, shouldValidate: true });
    };

    if (!items.length) return null;

    return (
        <Stack
            direction='row'
            spacing={1}
            flexWrap='wrap'
            useFlexGap
            sx={{ mt: 1 }}
        >
            {urls.map((src, i) => (
                <Box
                    key={i}
                    sx={{
                        position: 'relative',
                        width: 96,
                        height: 96,
                        borderRadius: 2,
                        overflow: 'hidden',
                        border: '1px solid',
                        borderColor: 'divider',
                    }}
                >
                    <img
                        src={src}
                        alt={`selected ${i + 1}`}
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                        }}
                    />
                    <IconButton
                        size='small'
                        onClick={() => removeAt(i)}
                        disabled={disabled}
                        sx={{
                            position: 'absolute',
                            top: 2,
                            right: 2,
                            bgcolor: 'rgba(0,0,0,0.4)',
                            color: theme.palette.neutral.main,
                            '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' },
                        }}
                        aria-label='remove photo'
                    >
                        <CloseIcon fontSize='small' />
                    </IconButton>
                </Box>
            ))}
        </Stack>
    );
};

export default PhotoPreviews;
